import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { posApi } from '@/api/pos.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { formatCurrency } from '@/utils/format';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';

const PAYMENT_LABELS: Record<string, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
  WALLET: 'Wallet',
  RAZORPAY: 'Razorpay',
  STRIPE: 'Stripe',
  PAYTM: 'Paytm',
  CREDIT: 'Credit',
};

export function PosReportsPage() {
  const store = useAppSelector((s) => s.app.selectedStore);
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);

  const {
    data: sales,
    isLoading: loadingSales,
    isError: salesError,
  } = useQuery({
    queryKey: ['pos-report-sales', store?.id, from, to],
    queryFn: () => posApi.salesReport(store!.id, from, to),
    enabled: !!store,
  });

  const {
    data: collection,
    isLoading: loadingCollection,
    isError: collectionError,
  } = useQuery({
    queryKey: ['pos-report-collection', store?.id, from, to],
    queryFn: () => posApi.collectionReport(store!.id, from, to),
    enabled: !!store,
  });

  const {
    data: returns,
    isLoading: loadingReturns,
    isError: returnsError,
  } = useQuery({
    queryKey: ['pos-report-returns', store?.id, from, to],
    queryFn: () => posApi.returnReport(store!.id, from, to),
    enabled: !!store,
  });

  if (!store) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Select a store.
      </div>
    );
  }

  const currency = store.currencyCode ?? 'INR';
  const fmt = (v: unknown) => formatCurrency(Number(v) || 0, currency);
  const card = 'rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900';
  const loading = loadingSales || loadingCollection || loadingReturns;
  const hasError = salesError || collectionError || returnsError;

  const byMethod = (collection?.byMethod ?? {}) as Record<string, number>;
  const methodEntries = Object.entries(byMethod).sort((a, b) => b[1] - a[1]);

  return (
    <div className="h-full overflow-auto p-6">
      <PageHeader
        title="POS Terminal Reports"
        description={`Shift-scoped sales, collections, and returns for ${store.name}`}
      />
      <div className="mt-4 flex items-end">
        <DateRangeFields
          className="flex-1"
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
        />
      </div>

      {hasError && (
        <p className="mt-4 text-sm text-red-600">Failed to load one or more report sections. Try again.</p>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <div className={card}>
          <p className="text-xs uppercase text-slate-500">Sales</p>
          {loadingSales ? (
            <Skeleton className="mt-2 h-8 w-32" />
          ) : (
            <>
              <p className="text-2xl font-bold">{fmt(sales?.totalSales)}</p>
              <p className="text-sm text-slate-500">{Number(sales?.transactionCount) || 0} transactions</p>
            </>
          )}
        </div>
        <div className={card}>
          <p className="text-xs uppercase text-slate-500">Collected</p>
          {loadingCollection ? (
            <Skeleton className="mt-2 h-8 w-32" />
          ) : (
            <>
              <p className="text-2xl font-bold">{fmt(collection?.totalCollected)}</p>
              {methodEntries.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No payments in range</p>
              ) : (
                <ul className="mt-3 space-y-1 text-sm">
                  {methodEntries.map(([method, amount]) => (
                    <li key={method} className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>{PAYMENT_LABELS[method] ?? method}</span>
                      <span className="font-medium tabular-nums">{fmt(amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <div className={card}>
          <p className="text-xs uppercase text-slate-500">Returns</p>
          {loadingReturns ? (
            <Skeleton className="mt-2 h-8 w-32" />
          ) : (
            <>
              <p className="text-2xl font-bold">{fmt(returns?.totalReturnAmount)}</p>
              <p className="text-sm text-slate-500">{Number(returns?.returnCount) || 0} returns</p>
            </>
          )}
        </div>
      </div>

      {!loading && !hasError && !sales?.transactionCount && !returns?.returnCount && (
        <EmptyState
          className="mt-8"
          title="No POS activity"
          description="Adjust the date range or complete sales at the terminal."
        />
      )}
    </div>
  );
}

